/**
 * Router and the auth gate.
 *
 * Every state is handled explicitly. The Base44 version had an `authError.type`
 * of "unknown" that fell through its `if/else` and rendered the full app
 * against a broken backend, and it called its login redirect from inside the
 * render body as a side effect.
 */

import { Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AlertTriangle, Loader2, LockKeyhole, SearchX } from 'lucide-react';
import { Toaster } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { UpdateToast } from '@/components/UpdateToast.jsx';
import { UpdateProvider, useAppUpdate } from '@/lib/update-context.jsx';
import { Layout } from '@/Layout.jsx';
import { AUTH_STATE, AuthProvider, useAuth } from '@/lib/auth-context.jsx';
import { queryClient } from '@/lib/query-client.js';
import { ThemeProvider } from '@/lib/theme-context.jsx';
import SignIn, { SetPassword } from '@/pages/SignIn.jsx';
import { landingPath, routes } from '@/routes.jsx';

function FullPageMessage({ icon: Icon, title, description, action }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-muted/40 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Icon className="h-6 w-6 text-muted-foreground" />
        </div>
        <h1 className="text-lg font-semibold">{title}</h1>
        {description ? <p className="mt-2 text-sm text-muted-foreground">{description}</p> : null}
        {action ? <div className="mt-6 flex justify-center">{action}</div> : null}
      </div>
    </div>
  );
}

function Loading({ label = 'Loading' }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">{label}...</span>
      </div>
    </div>
  );
}

/** Blocks a route the current profile has no permission for. */
function RequirePermission({ permission, children }) {
  const { can } = useAuth();
  const location = useLocation();

  if (can(permission)) return children;

  return (
    <div className="px-4 py-16 sm:px-6">
      <Alert variant="destructive" className="mx-auto max-w-lg">
        <LockKeyhole className="h-4 w-4" />
        <AlertTitle>You do not have access to this page</AlertTitle>
        <AlertDescription className="mt-2">
          <p>
            Your account does not include the permission needed for{' '}
            <span className="font-medium">{location.pathname}</span>. An administrator can grant it
            from User Management.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <a href="/">Back to the start</a>
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}

function NotFound() {
  return (
    <div className="px-4 py-16 sm:px-6">
      <div className="mx-auto max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <SearchX className="h-6 w-6 text-muted-foreground" />
        </div>
        <h1 className="text-lg font-semibold">Page not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          That address does not match anything in StockWise.
        </p>
        <Button asChild className="mt-6">
          <a href="/">Back to the start</a>
        </Button>
      </div>
    </div>
  );
}

function AuthenticatedApp() {
  const { can } = useAuth();
  const home = landingPath(can);

  return (
    <Layout>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          {routes.map(({ path, permission, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <RequirePermission permission={permission}>
                  <Component />
                </RequirePermission>
              }
            />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

/**
 * Every route change asks (throttled) whether a newer build is waiting, so the
 * update prompt appears while the user is between things — instead of the
 * deploy landing on them mid-page an hour later.
 */
function UpdateCheckOnNavigate() {
  const location = useLocation();
  const { checkForUpdates } = useAppUpdate();

  useEffect(() => {
    checkForUpdates();
  }, [location.pathname, checkForUpdates]);

  return null;
}

function Gate() {
  const { state, problem, signOut } = useAuth();

  switch (state) {
    case AUTH_STATE.LOADING:
      return (
        <div className="min-h-dvh">
          <Loading label="Signing in" />
        </div>
      );

    case AUTH_STATE.SIGNED_OUT:
      return <SignIn />;

    case AUTH_STATE.NOT_PROVISIONED:
      return (
        <FullPageMessage
          icon={LockKeyhole}
          title="This account is not active yet"
          description={
            problem ||
            'An administrator needs to activate your account before you can use StockWise.'
          }
          action={
            <Button variant="outline" onClick={signOut}>
              Sign out
            </Button>
          }
        />
      );

    case AUTH_STATE.UNAVAILABLE:
      return (
        <FullPageMessage
          icon={AlertTriangle}
          title="StockWise is not reachable"
          description={problem || 'The server did not respond. Try again in a moment.'}
          action={<Button onClick={() => window.location.reload()}>Try again</Button>}
        />
      );

    default:
      return <AuthenticatedApp />;
  }
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <UpdateProvider>
          <BrowserRouter>
            <AuthProvider>
              {/*
                Above the gate, not inside it.
                These two screens were registered twice — once for signed-in
                users and once for signed-out — because a Supabase invite link
                carried a session in the URL fragment and the app could not
                predict which branch would render by the time it arrived. They
                are self-sufficient now: the token is in the query string and
                neither screen needs a session to work. Registering them once,
                ahead of the gate, also means someone already signed in who
                clicks a reset link lands on the reset screen rather than the
                app shell.
              */}
              <UpdateCheckOnNavigate />
              <Routes>
                <Route path="/accept-invite" element={<SetPassword />} />
                <Route path="/reset-password" element={<SetPassword />} />
                <Route path="*" element={<Gate />} />
              </Routes>
              <UpdateToast />
            </AuthProvider>
          </BrowserRouter>
        </UpdateProvider>
        {/* Below the status bar when installed to an iPhone home screen. */}
        <Toaster
          richColors
          closeButton
          position="top-right"
          offset={{ top: 'calc(24px + env(safe-area-inset-top))' }}
          mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
        />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
